import { useEffect, useRef } from "react";

export const RESULT_PANEL_UPDATE_EVENT = "RESULT_PANEL_UPDATE";

export interface ResultPanelUpdatePayload {
  sportType?: string;
  betType?: string;
  matchId?: string;
  reason?: string;
}

export interface UseResultPanelSocketRefreshOptions {
  sportType: string;
  betTypes?: string[];
  onRefresh: () => void | Promise<void>;
  enabled?: boolean;
  debounceMs?: number;
}

/**
 * Soft-refresh Result panel lists on socket invalidate + one refresh when tab becomes visible.
 * No continuous polling while the page stays open.
 */
export function useResultPanelSocketRefresh({
  sportType,
  betTypes,
  onRefresh,
  enabled = true,
  debounceMs = 1000,
}: UseResultPanelSocketRefreshOptions): void {
  const onRefreshRef = useRef(onRefresh);
  const refreshingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sportTypeRef = useRef(sportType);
  const betTypesRef = useRef(betTypes);

  onRefreshRef.current = onRefresh;
  sportTypeRef.current = sportType;
  betTypesRef.current = betTypes;

  useEffect(() => {
    if (!enabled) return undefined;

    const runRefresh = async () => {
      if (refreshingRef.current) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      refreshingRef.current = true;
      try {
        await onRefreshRef.current();
      } catch (_) {
        /* ignore soft-refresh errors */
      } finally {
        refreshingRef.current = false;
      }
    };

    const scheduleRefresh = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        runRefresh();
      }, debounceMs);
    };

    const matchesPayload = (detail: ResultPanelUpdatePayload | undefined): boolean => {
      const payload = detail || {};
      const currentSport = (sportTypeRef.current || "").toLowerCase();
      const payloadSport = (payload.sportType || "").toLowerCase();
      if (currentSport && payloadSport && currentSport !== payloadSport) {
        return false;
      }
      const allowed = betTypesRef.current;
      if (Array.isArray(allowed) && allowed.length > 0 && payload.betType) {
        return allowed.includes(payload.betType);
      }
      return true;
    };

    const onInvalidate = (event: Event) => {
      const customEvent = event as CustomEvent<ResultPanelUpdatePayload>;
      if (!matchesPayload(customEvent?.detail)) return;
      scheduleRefresh();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        scheduleRefresh();
      }
    };

    window.addEventListener(RESULT_PANEL_UPDATE_EVENT, onInvalidate);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener(RESULT_PANEL_UPDATE_EVENT, onInvalidate);
      document.removeEventListener("visibilitychange", onVisibility);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, debounceMs]);
}
